import { omit } from 'lodash-es'
import { getSnapshot, types } from 'mobx-state-tree'
import { isLength } from 'validator'

import { FormStringFieldModel } from '../FormField/FormFieldModel'
import { IFormValidation } from '../FormField/FormValidation'
import { LocationPoint, TLocationPoint } from './LocationPoint'
import { isAddress, isPort } from './locationsUtils'
import { PortOrAddress } from './Place'

export interface ILocationResJSON {
  data: ILocationModelJSON[]
  total: number
}

export interface ILocationModelJSON {
  _id: string
  organizationId: string
  name: string
  companyName?: string
  contactIds?: string[]
  place: PortOrAddress
  notes?: string
  createdAt: string
  updatedAt: string
  deletedAt?: string
}

const getUpdatedPlace = (snapshot) => {
  const { place, companyName } = snapshot

  if (!place) {
    return
  }
  const validPlace = {
    _id: place._id,
    types: place.types,
    name: place.name,
    countryCode: place.countryCode,
  }
  if (place.location && place.location.coordinates) {
    return {
      ...validPlace,
      location: {
        longitude: place.location.coordinates[0],
        latitude: place.location.coordinates[1],
      },
    }
  }
  if (place.googlePlace) {
    return {
      googlePlace: place.googlePlace,
      companyName,
    }
  }
  if (place.place_id) {
    return { googlePlace: place.place_id, companyName }
  }
  return { ...validPlace, companyName }
}

const dirtyEnabledFields = ['organizationId', 'name', 'place', 'notes']

const PlaceModel = types
  .model('PlaceModel', {
    value: types.maybe(LocationPoint),
    isDirty: types.optional(types.boolean, false),
  })
  .preProcessSnapshot((snapshot) => {
    if (snapshot) {
      return { value: snapshot, isDirty: true }
    }
    return { value: snapshot }
  })
  .views((self) => ({
    get isInvalid(): boolean {
      return self.isDirty && !self.value
    },
  }))
  .actions((self) => ({
    postProcessSnapshot() {
      return self.value ? getSnapshot(self.value) : null
    },
    setValue(place) {
      self.value = place
      self.isDirty = true
    },
  }))

const LocationBase = types
  .model('LocationBase', {
    customerId: types.maybe(types.string),
    organizationId: types.optional(FormStringFieldModel, undefined),
    name: FormStringFieldModel,
    companyName: types.optional(types.union(types.string, types.literal(undefined)), undefined),
    contactIds: types.optional(types.array(types.string), []),
    place: PlaceModel,
    notes: FormStringFieldModel,
    createdAt: types.maybe(types.string),
    updatedAt: types.maybe(types.string),
    deletedAt: types.maybe(types.string),
  })
  .volatile(() => ({
    triedSubmit: false,
  }))
  .actions((self) => ({
    setOrganizationId(organizationId: string): void {
      self.organizationId.setValue(organizationId)
    },
    setName(name: string): void {
      self.name.setValue(name)
    },
    setCompanyName(companyName: string): void {
      self.companyName = companyName
    },
    setContactId(contactId: string): void {
      self.contactIds.push(contactId)
    },
    setPlace(place: TLocationPoint): void {
      self.place.setValue(place)
    },
    setNotes(notes: string): void {
      self.notes.setValue(notes)
    },
    removeContact(contactId: string) {
      self.contactIds.remove(contactId)
    },
    submit(): void {
      self.triedSubmit = true
      dirtyEnabledFields.forEach((dirtyEnabledField) => {
        self[dirtyEnabledField].isDirty = true
      })
    },
  }))
  .views((self) => ({
    // validation views
    validateOrganizationId(): IFormValidation {
      return {
        isInvalid: self.organizationId.isDirty && !self.organizationId.value,
        message: 'directory-validation_organization_select',
      }
    },
    validateName(): IFormValidation {
      return {
        isInvalid: self.name.isDirty && !isLength(self.name.value, { min: 1, max: 255 }),
        message: 'directory-validation_name',
      }
    },
    validatePlace(): IFormValidation {
      return {
        isInvalid: self.place.isInvalid,
        message: 'directory-validation_location_select',
      }
    },
    validateNotes(): IFormValidation {
      return {
        isInvalid: !isLength(self.notes.value, { min: 0, max: 500 }),
        message: 'directory-validation_notes',
      }
    },
  }))
  .views((self) => ({
    get string(): string {
      // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
      return `${self.name.value}, ${self.place.value.string}`
    },
    get isValid(): boolean {
      const validationField = ['validateOrganizationId', 'validateName', 'validatePlace', 'validateNotes']
      return (
        self.organizationId.isDirty &&
        self.name.isDirty &&
        self.place.isDirty &&
        validationField.every((field) => !self[field]().isInvalid)
      )
    },
    get locationJSON(): Partial<ILocationModelJSON> {
      const fieldsToRemove = ['createdAt', 'updatedAt', 'deletedAt', 'companyName']
      const snapshot = getSnapshot(self)
      if (snapshot._id) {
        return {
          ...omit(snapshot, fieldsToRemove),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
          place: getUpdatedPlace(snapshot) as any, // / because there is deviation on what we get back from /places api and /locations api
        }
      }
      return {
        ...omit(snapshot, [...fieldsToRemove, '_id']),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
        place: getUpdatedPlace(snapshot) as any,
      }
    },
  }))

export const Location = types.compose(
  'Location',
  types.model({
    _id: types.maybe(types.identifier(types.string)),
  }),
  LocationBase
)
export type TLocation = typeof Location.Type

export const SelectableLocation = types.compose(
  'SelectableLocation',
  types.model({
    _id: types.maybe(types.string),
  }),
  LocationBase
)

export const LocationsModel = types.model('LocationsModel', {
  data: types.array(Location),
  total: types.number,
})

export type TLocationsModel = typeof LocationsModel.Type

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
export function isLocation(maybeLocation: any): maybeLocation is ILocationModelJSON {
  if (typeof maybeLocation !== 'object') return false
  return (
    isPort(maybeLocation.place) ||
    isAddress(maybeLocation.place) ||
    (!!maybeLocation.place && !!maybeLocation.place.googlePlace)
  )
}
